/**
 * Payments with no order behind them.
 *
 * A Zelle or Venmo payment the matcher logged and could not attach. It is not an order, so
 * it is deliberately not an order row: it has no number, no customer record and no status,
 * and putting it in the table would mean eight columns of blanks and a row that cannot be
 * selected, printed or opened.
 *
 * What it does have is an amount, a payer, a time and the closest pending orders by amount
 * and name. "This is it" opens that candidate's drawer with Mark paid already filled in
 * from this payment, which is the whole job: two clicks from a payment nobody could place
 * to an order that is paid.
 */
import type { UnmatchedPaymentRow } from '@elutide/shared';
import { Button, Pill } from '@/components';
import { formatMoney, fullTimestamp, relativeAge } from '@/lib/format';

export interface UnmatchedPaymentsProps {
  rows: readonly UnmatchedPaymentRow[];
  now: number;
  /** True on the Needs attention queue, where this is the work rather than a warning. */
  expanded: boolean;
  /** Open a candidate order with Mark paid prefilled from this payment. */
  onClaim: (payment: UnmatchedPaymentRow, orderId: number) => void;
  /** Take the operator to the queue where all of these are worked. */
  onSeeAll: () => void;
}

/**
 * How many to show outside Needs attention.
 *
 * Production carries seventy-six of these, almost all of them Chase card capture rows with
 * no candidate order, and a seventy-six item red block above the table on every visit to
 * Orders would train the owner to scroll past the one that matters. Three plus a count is
 * a warning; seventy-six is wallpaper.
 */
const PREVIEW = 3;

export function UnmatchedPayments({
  rows,
  now,
  expanded,
  onClaim,
  onSeeAll,
}: UnmatchedPaymentsProps) {
  if (rows.length === 0) return null;
  const shown = expanded ? rows : rows.slice(0, PREVIEW);

  return (
    <section className="ord-unmatched" aria-label="Payments with no order">
      <header className="ord-unmatched__head">
        <h3 className="o-section-title">Payments with no order</h3>
        <Pill tone="crit">{rows.length}</Pill>
      </header>

      <ul className="ord-unmatched__list" role="list">
        {shown.map((payment) => (
          <li key={payment.id} className="ord-unmatched__item">
            <div className="ord-unmatched__facts">
              <span className="ord-total">{formatMoney(payment.amount_cents)}</span>
              <span className="ord-name">{payment.payer ?? 'Unnamed payer'}</span>
              <span className="ord-sub">
                <span className="ord-badge ord-badge--matcher">{payment.source}</span>
                <span title={fullTimestamp(payment.received_at)}>
                  received {relativeAge(payment.received_at, now)}
                </span>
              </span>
              {payment.memo ? <span className="ord-sub ord-memo">{payment.memo}</span> : null}
            </div>

            <div className="ord-unmatched__candidates">
              {payment.candidate_order_ids.length === 0 ? (
                <span className="ord-sub">
                  No pending order matches this amount. Search the payer name above.
                </span>
              ) : (
                payment.candidate_order_ids.map((orderId) => (
                  <span key={orderId} className="ord-candidate">
                    <span className="ord-num">{orderId}</span>
                    <Button size="sm" variant="primary" onClick={() => onClaim(payment, orderId)}>
                      This is it
                    </Button>
                  </span>
                ))
              )}
            </div>
          </li>
        ))}
      </ul>

      {shown.length < rows.length ? (
        <footer className="ord-unmatched__foot">
          <span className="ord-sub">
            {rows.length - shown.length} more payments have no order behind them.
          </span>
          <Button size="sm" variant="ghost" onClick={onSeeAll}>
            Open Needs attention
          </Button>
        </footer>
      ) : null}
    </section>
  );
}
